import React, { useState } from "react";
import { SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { OverviewPanel } from "./OverviewPanel";
import { OverviewCpe, OverviewSites } from "../Types";
import { ServiceHealth, ServiceScope, SiteSummary, worseHealth } from "./overviewModel";
import { SitesMap } from "./SitesMap";
import { SiteSelection, SitesDiagram } from "./SitesDiagram";
import { MapCpeLatency, MapLatency, isCpePlaced } from "./mapModel";
import { isExtenderPopEnabled } from "@shared/config/featureFlags";

/**
 * The customer's CPEs the map draws: under "All services", "Ports" and
 * "CPE", and only while the CPE view is enabled.
 */
function cpesInScope(locations: OverviewSites | null, scope: ServiceScope): OverviewCpe[] {
  if (!isExtenderPopEnabled() || (scope !== "all" && scope !== "ports" && scope !== "cpe")) return [];
  return locations?.cpes ?? [];
}

function hasMapData(locations: OverviewSites | null, cpes: OverviewCpe[]): boolean {
  return (
    (locations?.sites ?? []).some((location) => location.lat !== null && location.lng !== null) ||
    cpes.some(isCpePlaced)
  );
}

function siteHealth(site: SiteSummary): ServiceHealth {
  return site.portsDown > 0 ? "down" : "up";
}

/**
 * The tenant's topology — sites, DCIs, clouds. On the map when any site has a location on file; as a diagram
 * of the same parts when none has, so a tenant without GPS still sees how its
 * sites connect.
 */
export function SitesPanel({
  sites,
  locations,
  scope,
  serviceHealthBySite,
  cloudHealthBySite,
  selectedNodeKey,
  onSelectNode,
  toolbar,
  isLoading,
  foldable = false,
  latency = null,
  cpeLatency = null,
}: {
  sites: SiteSummary[];
  scope: ServiceScope;
  /** Start with the map folded behind a button: the stacked (phone) layout. */
  foldable?: boolean;
  /** Worst state of the services in `scope` at each site (serviceHealthBySite). */
  serviceHealthBySite: Record<string, ServiceHealth>;
  /** Worst state of the cloud services at each site, for the cloud lines. */
  cloudHealthBySite: Record<string, ServiceHealth>;
  selectedNodeKey: string | null;
  onSelectNode: (site: SiteSelection | null) => void;
  /** The scope chips, in the header so they cost no row of their own. */
  toolbar: React.ReactNode;
  /** Names, coordinates and DCI links; null when the page reads the four endpoints. */
  locations: OverviewSites | null;
  isLoading: boolean;
  /** RTT between sites for the DCI lines; null for a viewer the matrix is not for. */
  latency?: MapLatency | null;
  /** RTT of the tenant's Backhaul legs for the CPE lines; null when there is none to show. */
  cpeLatency?: MapCpeLatency | null;
}) {
  const [isMapOpen, setIsMapOpen] = useState(false);
  const isFolded = foldable && !isMapOpen;
  return (
    <OverviewPanel
      title="Topology"
      action={toolbar}
    >
      {foldable && sites.length > 0 && (
        <button
          type="button"
          aria-expanded={isMapOpen}
          onClick={() => setIsMapOpen((open) => !open)}
          className={`flex min-h-11 w-full items-center justify-between rounded-md border border-semantic-border-primary px-3 text-sm font-semibold text-semantic-text-primary hover:bg-semantic-hover-primary ${
            isMapOpen ? "mb-3" : ""
          }`}
        >
          <span>
            {isMapOpen ? "Hide map" : "Show map"}
            <span className="ml-2 font-normal text-semantic-text-secondary">
              {sites.length} {sites.length === 1 ? "site" : "sites"}
            </span>
          </span>
          <span aria-hidden>{isMapOpen ? "▴" : "▾"}</span>
        </button>
      )}
      {!isFolded && <SitesBody
        sites={sites}
        locations={locations}
        scope={scope}
        serviceHealthBySite={serviceHealthBySite}
        cloudHealthBySite={cloudHealthBySite}
        selectedNodeKey={selectedNodeKey}
        onSelectNode={onSelectNode}
        isLoading={isLoading}
        latency={latency}
        cpeLatency={cpeLatency}
      />}
    </OverviewPanel>
  );
}

function SitesBody({
  sites,
  locations: scopeLocations,
  scope,
  serviceHealthBySite,
  cloudHealthBySite,
  selectedNodeKey,
  onSelectNode,
  isLoading,
  latency,
  cpeLatency,
}: {
  sites: SiteSummary[];
  locations: OverviewSites | null;
  scope: ServiceScope;
  serviceHealthBySite: Record<string, ServiceHealth>;
  cloudHealthBySite: Record<string, ServiceHealth>;
  selectedNodeKey: string | null;
  onSelectNode: (site: SiteSelection | null) => void;
  isLoading: boolean;
  latency: MapLatency | null;
  cpeLatency: MapCpeLatency | null;
}) {
  if (isLoading) {
    return (
      <div aria-busy="true">
        <SkeletonBlock h={260} roundedClass="rounded-lg" decorative />
      </div>
    );
  }
  if (sites.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-semantic-text-secondary">No ports provisioned yet.</p>
    );
  }
  // A site shows the worst of what is selected: its services, plus its ports'
  // own state under "All services" and "Ports".
  const countsPorts = scope === "all" || scope === "ports";
  const healthBySite = Object.fromEntries(
    sites.map((site) => {
      const services = serviceHealthBySite[site.id] ?? "unknown";
      const health = countsPorts ? worseHealth(siteHealth(site), services) : services;
      return [site.id, health === "unknown" ? "up" : health];
    })
  );
  const cpes = cpesInScope(scopeLocations, scope);
  // A Backhaul landing is drawn only for the CPEs that land there.
  const locations =
    scopeLocations && cpes.length === 0
      ? { ...scopeLocations, sites: scopeLocations.sites.filter((site) => !site.backhaulOnly) }
      : scopeLocations;
  if (!hasMapData(locations, cpes) || !locations) {
    return (
      <SitesDiagram
        sites={sites}
        locations={locations}
        healthBySite={healthBySite}
        cloudHealthBySite={cloudHealthBySite}
        scope={scope}
        selectedKey={selectedNodeKey}
        onSelectNode={onSelectNode}
        latency={latency}
      />
    );
  }
  const unplaced = locations.sites.filter((location) => location.lat === null || location.lng === null);
  const unplacedCpes = cpes.filter((cpe) => !isCpePlaced(cpe));
  return (
    <div className="space-y-2">
      <SitesMap
        locations={locations}
        cpes={cpes}
        healthBySite={healthBySite}
        cloudHealthBySite={cloudHealthBySite}
        scope={scope}
        selectedKey={selectedNodeKey}
        onSelectNode={onSelectNode}
        latency={latency}
        cpeLatency={cpeLatency}
      />
      {unplaced.length > 0 && (
        <p className="text-xs text-semantic-text-secondary">
          Not on the map (no location on file):{" "}
          <span className="text-semantic-text-primary">
            {unplaced.map((location) => location.displayName).join(", ")}
          </span>
        </p>
      )}
      {unplacedCpes.length > 0 && (
        <p className="text-xs text-semantic-text-secondary">
          CPEs not on the map (no GPS yet):{" "}
          <span className="text-semantic-text-primary">
            {unplacedCpes.map((cpe) => `${cpe.name} (${cpe.serviceId})`).join(", ")}
          </span>
        </p>
      )}
    </div>
  );
}
